import { lazyWithRetry as lazy } from '../utils/lazyWithRetry';
// Home is the entry route on '/' — eager-imported so first paint doesn't wait for a chunk.
import Home from '../pages/Home';

const Settings = lazy(() => import('../pages/Settings/Settings'));
const Language = lazy(() => import('../pages/Settings/Language'));
const LinkedAccounts = lazy(() => import('../pages/Settings/LinkedAccounts'));
const Security = lazy(() => import('../pages/Settings/Security'));
const FAQ = lazy(() => import('../pages/FAQ'));
const About = lazy(() => import('../pages/About'));
const FollowingFeed = lazy(() => import('../pages/SocialNetwork/FollowingFeed'));
const Risks = lazy(() => import('../pages/Risks'));
const Statistics = lazy(() => import('../pages/Statistics/Statistics'));
const LoanIssuance = lazy(() => import(/* webpackPrefetch: true */ '../pages/LoansInvest/LoanIssuance'));
const LoanOvernight = lazy(() => import(/* webpackPrefetch: true */ '../pages/Overnight/LoanOvernight'));
const ConvertOvernight = lazy(() => import('../pages/Overnight/ConvertOvernight'));
const OvernightPartners = lazy(() => import('../pages/Overnight/OvernightPartners'));
const LoanInsure = lazy(() => import('../pages/LoanInsure'));
const SalesCertificates = lazy(() => import('../pages/SalesCertificates'));
const Certificate = lazy(() => import('../pages/Certificate'));
const LoanExchange = lazy(() => import('../pages/LoanExchange'));
const LoanPage = lazy(() => import('../pages/LoanPage'));
const Partners = lazy(() => import('../pages//Partners/Partners'));
const PartnersStatistics = lazy(() => import('../pages//Partners/PartnersStatistics'));
const PartnersLevelList = lazy(() => import('../pages/Partners/PartnersLevelList'));
const TopSeniorPartners = lazy(() => import('../pages/Partners/TopSeniorPartners'));
const Invest = lazy(() => import('../pages/Invest'));
const InvestPayment = lazy(() => import('../pages/InvestPayment'));
const Marketing = lazy(() => import('../pages/Marketing'));
const WebTokens = lazy(() => import('../pages/WebTokens'));
const WebBuyback = lazy(() => import('../pages/WebBuyback/WebBuyback'));
const Tasks = lazy(() => import('../pages/Tasks/Tasks'));
const UserTasks = lazy(() => import('../pages/UserTasks/UserTasks'));
const CreateTask = lazy(() => import('../pages/UserTasks/CreateTask'));
const WithdrawalPage = lazy(() => import(/* webpackPrefetch: true */ '../pages/Withdrawal/Withdrawal'));
const Auction = lazy(() => import('../pages/Auction/Auction'));
const Notifications = lazy(() => import('../pages/Notifications/Notifications'));
// Home: eager import declared at top of file.
const Premium = lazy(() => import('../pages/Premium/Premium'));
const InvestPartnerBalance = lazy(() => import('../pages/Partners/InvestPartnerBalance'));
const BankGame = lazy(() => import('../pages/Game/BankGame'));
const PartnerDetails = lazy(() => import('../components/PartnerDetails'));
const SinglePost = lazy(() => import('../components/SinglePost'));
const NotificationSettings = lazy(() => import('../components/NotificationSettings'));
const TransactionHistory = lazy(() => import('../components/history'));
const Loans = lazy(() => import('../components/loans'));
const LastAuctionWinners = lazy(() => import('../pages/Auction/LastAuctionWinners'));
const Contest = lazy(() => import('../pages/Contests/Contest'));
const ChatWindow = lazy(() => import('../pages/Chat/ChatWindow'));
const Achievements = lazy(() => import('../pages/Achievements/Achievements'));
const TaskDetails = lazy(() => import('../pages/UserTasks/TaskDetails'));
const ReferralContest = lazy(() => import('../pages/Contests/ReferralContest'));
const Career = lazy(() => import('../pages/Career/Career'));
const Rosca = lazy(() => import('../pages/Rosca/Rosca'));
const RoscaCircles = lazy(() => import('../pages/Rosca/RoscaCircles'));
const RoscaCircleDetails = lazy(() => import('../pages/Rosca/RoscaCircleDetails'));
const Unfreeze = lazy(() => import('../pages/Unfreeze/Unfreeze'));
const PostFreezeWithdraw = lazy(() => import('../pages/PostFreeze/PostFreezeWithdraw'));
const KredafiMenu = lazy(() => import('../kredafi/KredafiMenu'));
const KredafiProfile = lazy(() => import('../kredafi/KredafiProfile'));

const routes = [
  { path: '/settings', component: Settings },
  { path: '/settings/security', component: Security },
  { path: '/settings/language', component: Language },
  { path: '/settings/accounts', component: LinkedAccounts },
  { path: '/settings/notifications', component: NotificationSettings },
  { path: '/settings/faq', component: FAQ },
  { path: '/settings/about', component: About },
  { path: '/settings/risks', component: Risks },
  { path: '/notifications', component: Notifications },
  { path: '/statistics', component: Statistics },
  { path: '/loan-issuance', component: LoanIssuance },
  { path: '/loan-overnight', component: LoanOvernight },
  { path: '/overnight/convert', component: ConvertOvernight },
  { path: '/loan-overnight/partners', component: OvernightPartners },
  { path: '/loan-insure', component: LoanInsure },
  { path: '/certificates/sales', component: SalesCertificates },
  { path: '/certificates/sales/:loanId', component: Certificate },
  { path: '/p2p-loans', component: LoanExchange },
  { path: '/loan/:loanId', component: LoanPage },
  { path: '/auction', component: Auction },
  { path: '/profile', component: KredafiProfile },
  { path: '/partners', component: Partners },
  { path: '/partners/statistics', component: PartnersStatistics },
  { path: '/partners/level/:level', component: PartnersLevelList },
  { path: '/partners/top-senior', component: TopSeniorPartners },
  { path: '/partners/:userId', component: PartnerDetails },
  { path: '/feed', component: FollowingFeed },
  { path: '/post/:postId', component: SinglePost },
  { path: '/invest', component: Invest },
  { path: '/invest/payment', component: InvestPayment },
  { path: '/invest/marketing', component: Marketing },
  { path: '/web-tokens', component: WebTokens },
  { path: '/web-buyback', component: WebBuyback },
  { path: '/web-tokens/tasks', component: Tasks },
  { path: '/user-tasks', component: UserTasks },
  { path: '/user-tasks/create-task', component: CreateTask },
  { path: '/user-tasks/task-details/:taskId', component: TaskDetails },
  { path: '/withdraw', component: WithdrawalPage },
  { path: '/bank-game', component: BankGame },
  { path: '/premium', component: Premium },
  { path: '/invest-partners-balance', component: InvestPartnerBalance },
  { path: '/history', component: TransactionHistory },
  { path: '/loans', component: Loans },
  { path: '/last-auction-winners', component: LastAuctionWinners },
  { path: '/contest', component: Contest },
  { path: '/invest-contest', component: ReferralContest },
  { path: '/career', component: Career },
  { path: '/rosca', component: Rosca },
  { path: '/rosca/circles', component: RoscaCircles },
  { path: '/rosca/circle/:id', component: RoscaCircleDetails },
  { path: '/unfreeze', component: Unfreeze },
  { path: '/post-freeze', component: PostFreezeWithdraw },
  { path: '/menu', component: KredafiMenu },
  { path: '/', component: Home },
  { path: '/chat/:userId', component: ChatWindow },
  { path: '/achievements', component: Achievements },
  { path: '*', component: Home },
];

export default routes;
